import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Snackbar,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import paymentMethodService from '../services/paymentMethodService';

function PaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    icon: '',
    isActive: true,
    config: {},
  });

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const data = await paymentMethodService.getAll();
      setPaymentMethods(data);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des modes de paiement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await paymentMethodService.toggleActive(id);
      await loadPaymentMethods();
      showSnackbar('Statut mis à jour avec succès');
    } catch (error) {
      showSnackbar('Erreur lors de la mise à jour du statut', 'error');
    }
  };

  const handleOpenDialog = (method = null) => {
    if (method) {
      setEditingMethod(method);
      setFormData({
        code: method.code || '',
        name: method.name || '',
        description: method.description || '',
        icon: method.icon || '',
        isActive: method.isActive || false,
        config: method.config || {},
      });
    } else {
      setEditingMethod(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        icon: '',
        isActive: true,
        config: {},
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMethod(null);
  };

  const handleSave = async () => {
    try {
      if (editingMethod) {
        await paymentMethodService.update(editingMethod.id, formData);
        showSnackbar('Mode de paiement mis à jour avec succès');
      } else {
        await paymentMethodService.create(formData);
        showSnackbar('Mode de paiement créé avec succès');
      }
      await loadPaymentMethods();
      handleCloseDialog();
    } catch (error) {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce mode de paiement ?')) {
      try {
        await paymentMethodService.delete(id);
        await loadPaymentMethods();
        showSnackbar('Mode de paiement supprimé avec succès');
      } catch (error) {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(paymentMethods);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Mettre à jour les ordres
    const updatedOrders = items.map((item, index) => ({
      id: item.id,
      displayOrder: index + 1,
    }));

    setPaymentMethods(items);

    try {
      await paymentMethodService.updateOrder(updatedOrders);
      showSnackbar('Ordre mis à jour avec succès');
    } catch (error) {
      showSnackbar('Erreur lors de la mise à jour de l\'ordre', 'error');
      await loadPaymentMethods();
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleConfigChange = (key, value) => {
    setFormData({
      ...formData,
      config: {
        ...formData.config,
        [key]: value,
      },
    });
  };

  const renderConfigFields = () => {
    const { code } = formData;

    if (code === 'bank_transfer') {
      return (
        <>
          <TextField
            label="IBAN"
            fullWidth
            margin="normal"
            value={formData.config?.iban || ''}
            onChange={(e) => handleConfigChange('iban', e.target.value)}
          />
          <TextField
            label="BIC"
            fullWidth
            margin="normal"
            value={formData.config?.bic || ''}
            onChange={(e) => handleConfigChange('bic', e.target.value)}
          />
          <TextField
            label="Bénéficiaire"
            fullWidth
            margin="normal"
            value={formData.config?.beneficiary || ''}
            onChange={(e) => handleConfigChange('beneficiary', e.target.value)}
          />
          <TextField
            label="Banque"
            fullWidth
            margin="normal"
            value={formData.config?.bank || ''}
            onChange={(e) => handleConfigChange('bank', e.target.value)}
          />
        </>
      );
    }

    if (code === 'card' || code === 'apple_pay' || code === 'google_pay') {
      return (
        <TextField
          label="Provider (ex: stripe)"
          fullWidth
          margin="normal"
          value={formData.config?.provider || ''}
          onChange={(e) => handleConfigChange('provider', e.target.value)}
        />
      );
    }

    return null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Modes de paiement</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Ajouter un mode de paiement
        </Button>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="payment-methods">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {paymentMethods.map((method, index) => (
                <Draggable key={method.id} draggableId={String(method.id)} index={index}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        mb: 2,
                        bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                        border: snapshot.isDragging ? '2px solid' : 'none',
                        borderColor: 'primary.main',
                      }}
                    >
                      <CardContent>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item {...provided.dragHandleProps}>
                            <DragIcon sx={{ cursor: 'grab', color: 'text.secondary' }} />
                          </Grid>
                          <Grid item>
                            <Typography variant="h3" component="span">
                              {method.icon}
                            </Typography>
                          </Grid>
                          <Grid item xs>
                            <Typography variant="h6">{method.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {method.description}
                            </Typography>
                            <Chip
                              label={method.code}
                              size="small"
                              sx={{ mt: 1 }}
                              color="primary"
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={method.isActive}
                                  onChange={() => handleToggleActive(method.id)}
                                  color="primary"
                                />
                              }
                              label={method.isActive ? 'Actif' : 'Inactif'}
                            />
                          </Grid>
                          <Grid item>
                            <IconButton
                              onClick={() => handleOpenDialog(method)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDelete(method.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingMethod ? 'Modifier le mode de paiement' : 'Nouveau mode de paiement'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Code"
            fullWidth
            margin="normal"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            disabled={!!editingMethod}
            helperText="Code unique (ex: card, bank_transfer)"
          />
          <TextField
            label="Nom"
            fullWidth
            margin="normal"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <TextField
            label="Icône (emoji)"
            fullWidth
            margin="normal"
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            helperText="Ex: 💳, 🏦, 🍎"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Actif"
          />

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Configuration
          </Typography>
          {renderConfigFields()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSave} variant="contained">
            {editingMethod ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PaymentMethods;

